import './kitStyles';
import { useEffect, type RefObject } from 'react';

/**
 * WEB-3310 — клавиатура выпадающего меню кита, образец `useMenuKeys(ref, open,
 * setOpen)` эталона (`kit.jsx`, владелец 23.10 «рек»: свой кит, без библиотек).
 * Открылось — фокус на первом доступном пункте (`[role^=menuitem]` без
 * `aria-disabled=true`); ↓ ↑ — по кругу; Home / End — первый и последний;
 * Enter и Пробел — нажать пункт под фокусом; Esc — закрыть и вернуть фокус на
 * кнопку `[aria-haspopup]` внутри `ref`. Подключают меню и фишки (юнит 2а).
 */
export function useMenuKeys(
  ref: RefObject<HTMLElement>,
  open: boolean,
  setOpen: (open: boolean) => void,
): void {
  useEffect(() => {
    const root = ref.current;
    if (!open || !root) return undefined;
    const items = () =>
      Array.from(
        root.querySelectorAll<HTMLElement>('[role^=menuitem]:not([aria-disabled=true])'),
      );
    items()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      const list = items();
      if (!list.length) return;
      const i = list.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const d = e.key === 'ArrowDown' ? 1 : -1;
        list[(i + d + list.length) % list.length].focus();
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        list[e.key === 'Home' ? 0 : list.length - 1].focus();
      } else if ((e.key === 'Enter' || e.key === ' ') && i >= 0) {
        e.preventDefault();
        list[i].click();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        root.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
      }
    };
    root.addEventListener('keydown', onKey);
    return () => root.removeEventListener('keydown', onKey);
  }, [ref, open, setOpen]);
}
